import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getWebContainer, startDevServer } from '../lib/webcontainer';

const WebContainerContext = createContext(null);

export function WebContainerProvider({ children }) {
  const [webContainer, setWebContainer] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, booting, mounting, installing, running, error
  const [error, setError] = useState(null);

  useEffect(() => {
    // We don't boot automatically to save resources, 
    // but we could if we wanted to pre-warm it
  }, []);

  const boot = useCallback(async () => {
    if (webContainer) return webContainer;
    try {
      setStatus('booting');
      const instance = await getWebContainer();
      setWebContainer(instance);
      return instance;
    } catch (err) {
      console.error('Failed to boot WebContainer:', err);
      setError(err.message);
      setStatus('error');
      throw err;
    }
  }, [webContainer]);

  const runProject = useCallback(async (files) => {
    try {
      setStatus('mounting');
      const wc = await boot();
      await wc.mount(files);
      
      setStatus('installing');
      await startDevServer(wc, (url) => {
        setServerUrl(url);
        setStatus('running');
      });
    } catch (err) {
      console.error('Failed to run project:', err);
      setError(err.message);
      setStatus('error');
    }
  }, [boot]);

  const value = {
    webContainer,
    serverUrl,
    status,
    error,
    boot,
    runProject
  };

  return (
    <WebContainerContext.Provider value={value}>
      {children}
    </WebContainerContext.Provider>
  );
}

export function useWebContainer() {
  const context = useContext(WebContainerContext);
  if (!context) {
    throw new Error('useWebContainer must be used within a WebContainerProvider');
  }
  return context;
}
